// «SOLUȚIILE PROIECTULUI» (`/p/:id/solutii`, A1 și Corecția lui Stefan): tot
// arborele o dată, cu previzualizare. Înlocuiește tabla cu patru coloane,
// panoul intermediar al soluției și pagina de ramuri a structurii.
//
//   * sus: titlul, cu numărătoarea proiectului, «Arată încercările interne»,
//     «Oprește tot» / «Șterge toate soluțiile» și, în colțul din dreapta,
//     «Regenerează de la zero»; dedesubt cele patru plăci de etapă — Situație,
//     Arhitectură, Structură, Costuri —, fiecare cu ce e gata, în lucru, în
//     așteptare și ce nu trece; clicul pe o placă filtrează arborele;
//   * «În lucru acum» și «Terminate recent»: o bandă îngustă, fiecare linie
//     un link — și ce așteaptă, fiecare pe linia lui;
//   * arborele indentat: Amplasare → Arhitectură → Schiță și Soluția N (cu
//     două linii de fază), cifra-cheie, costul și meniul «⋯» — care arată
//     cascada ÎNAINTE de orice ștergere;
//   * dreapta: previzualizarea rândului ales, cu «Deschide soluția» și
//     «Pornește calculul detaliat». Dublu clic pe rând deschide direct. Cât nu
//     e ales niciun rând — la sosire, după X, Escape sau al doilea clic pe
//     rândul ales — acolo stau evenimentele proiectului, cu duratele lor.
//
// Arborele e o funcție pură a registrului (`nav/selectie.arbore`): numai
// `parent_solution_id` și `lineage` (Î6, Î7).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as api from "../api";
import { numar, ora, useT, useTn } from "../i18n";
import FazeBara, { SemnStare } from "../components/FazeBara";
import PrevizSolutie from "../components/PrevizSolutie";
import EvenimenteProiect from "./EvenimenteProiect";
import { Ic } from "../components/ui";
import { BandaDoarCitire, useRolProiect, useTipProiect } from "../components/rol";
import { useRegistru } from "../nav/Registru";
import { useActiuni } from "../nav/actiuni";
import MeniuLista from "../nav/MeniuLista";
import type { Disciplina, Rulare, SolutieRand } from "../nav/api";
import {
  arbore, areStructuraDesenata, caleCosturi, caleRand, eDeschis, elementeStructura,
  elementulRandului,
  fazeleElementului, fazeleRandului, inLucruAcum, lucrariVii, notaLucrarii, numaratori,
  numeElement, numeEtapa, numeLucrare, numeRand, numeVerificarePoarta, poateGeneraSolutia,
  poatePornireDetaliu, poateRefaceSubsolul, randulDeschis, stareElement,
  terminateRecent, type ElementStructura, type NumarEtapa,
} from "../nav/selectie";
import { money } from "../solutions/shared";
import { useStructure } from "../structure/StructureContext";
import "./ansamblu.css";

type Filtru = Disciplina | "costuri" | null;

/** Un rând al arborelui, deja așezat: ce se desenează și la ce adâncime. */
type Rand =
  | { fel: "solutie"; nivel: number; rand: SolutieRand; context: boolean }
  | { fel: "element"; nivel: number; element: ElementStructura; context: boolean }
  | { fel: "fantoma"; nivel: number; rulare: Rulare; disciplina: Disciplina };

const cheieRand = (r: Rand) =>
  r.fel === "solutie" ? r.rand.id : r.fel === "element" ? r.element.id : `job:${r.rulare.job_id}`;

export default function Ansamblu() {
  const t = useT();
  const tn = useTn();
  const navigate = useNavigate();
  const { projectId, registru, interne, setInterne, eroare, reciteste } = useRegistru();
  const { potScrie } = useRolProiect();
  const tip = useTipProiect();
  const actiuni = useActiuni();
  const [filtru, setFiltru] = useState<Filtru>(null);
  const [ales, setAles] = useState<string | null>(null);
  const [meniu, setMeniu] = useState<string | null>(null);
  const [opreste, setOpreste] = useState(false);
  const [sterg, setSterg] = useState(false);
  const [regenerez, setRegenerez] = useState(false);
  const [mesaj, setMesaj] = useState("");

  // CE S-A ȚINUT PE CLIENT SE UITĂ AICI (Stefan, 19.09.2026): de aici se
  // pornesc rulări și se șterg soluții, deci de aici se poate ieși spre ALT
  // document decât cel ținut.
  const { uitaCitite } = useStructure();
  useEffect(() => {
    uitaCitite();
  }, [uitaCitite]);
  // «Arată încercările interne» e al paginii ăsteia: la plecare, registrul
  // revine la rândurile vizibile.
  useEffect(() => () => setInterne(false), [setInterne]);

  const ab = useMemo(() => (registru ? arbore(registru, { interne }) : null),
                     [registru, interne]);
  const nr = useMemo(() => (registru ? numaratori(registru, { interne }) : null),
                     [registru, interne]);
  const lucru = useMemo(() => (registru ? inLucruAcum(registru, projectId) : []),
                        [registru, projectId]);
  const terminate = useMemo(() => (registru ? terminateRecent(registru, projectId) : []),
                            [registru, projectId]);

  // Rândurile arborelui, în ordinea de pe ecran. Cu un filtru, rândurile
  // etapei rămân, amontele lor rămâne ca CONTEXT (estompat), avalul pleacă.
  const randuri = useMemo<Rand[]>(() => {
    if (!ab) return [];
    const out: Rand[] = [];
    const vede = (d: Disciplina | "costuri") => !filtru || filtru === d;
    const elementCuCost = (e: ElementStructura) => e.fel === "solutie"
      && e.detaliu?.status === "ready" && e.detaliu.structura?.cost?.direct_eur != null;
    const arhitecturi = (lista: typeof ab.importate, nivel: number) => {
      for (const ra of lista) {
        const elemente = ra.structura.filter((e) =>
          filtru === "costuri" ? elementCuCost(e) : true);
        const areStructura = elemente.length > 0;
        const cuCeva = !filtru || filtru === "architecture"
          || ((filtru === "structure" || filtru === "costuri") && areStructura)
          || filtru === "site_plan";
        if (!cuCeva) continue;
        if (filtru !== "site_plan") {
          out.push({ fel: "solutie", nivel, rand: ra.rand,
                     context: !!filtru && filtru !== "architecture" });
        }
        if (vede("structure") || filtru === "costuri") {
          for (const e of elemente) {
            out.push({ fel: "element", nivel: nivel + 1, element: e, context: false });
          }
        }
      }
    };
    for (const a of ab.amplasari) {
      const areArh = a.arhitecturi.some((ra) => filtru !== "costuri"
        || ra.structura.some(elementCuCost));
      if (filtru && filtru !== "site_plan" && !areArh) continue;
      out.push({ fel: "solutie", nivel: 0, rand: a.rand,
                 context: !!filtru && filtru !== "site_plan" });
      if (filtru !== "site_plan") {
        arhitecturi(a.arhitecturi, 1);
        if (!filtru || filtru === "architecture") {
          for (const j of a.fantome) out.push({ fel: "fantoma", nivel: 1, rulare: j,
                                                 disciplina: "architecture" });
        }
      }
    }
    if (!filtru || filtru === "site_plan") {
      for (const j of ab.fantome) {
        out.push({ fel: "fantoma", nivel: 0, rulare: j,
                   disciplina: j.kind === "site_plan" ? "site_plan" : "architecture" });
      }
    }
    if (filtru !== "site_plan") arhitecturi(ab.importate, 0);
    if (!filtru || filtru === "structure") {
      for (const e of ab.orfane) out.push({ fel: "element", nivel: 0, element: e, context: false });
    }
    return out;
  }, [ab, filtru]);

  // Niciun rând ales implicit: panoul din dreapta arată atunci evenimentele.
  // Escape închide previzualizarea, ca X-ul ei.
  const randAles = randuri.find((r) => r.fel !== "fantoma" && cheieRand(r) === ales) ?? null;
  useEffect(() => {
    if (!ales) return;
    const tasta = (e: KeyboardEvent) => { if (e.key === "Escape") setAles(null); };
    window.addEventListener("keydown", tasta);
    return () => window.removeEventListener("keydown", tasta);
  }, [ales]);
  // al doilea clic pe rândul ales îl deselectează
  const comutaAles = (cheie: string) => setAles((v) => (v === cheie ? null : cheie));

  if (!registru || !nr || !ab) {
    return <div className="ans-pagina"><p className="note">
      {eroare ? t("ansamblu.esec") : t("comun.se_incarca")}</p></div>;
  }

  const total = nr.site_plan.total + nr.architecture.total + nr.structure.total;
  const inLucru = nr.site_plan.inLucru + nr.architecture.inLucru + nr.structure.inLucru;
  const asteapta = nr.site_plan.asteapta + nr.architecture.asteapta + nr.structure.asteapta;
  const areCeOpri = lucru.length > 0;
  // BUTONUL DIN ANTET urmează ordinea în care se lucrează (Stefan, 27.09.2026):
  // cât rulează ceva, «Oprește tot»; oprit totul, «Șterge toate soluțiile».
  // Ștergerea nu se oferă peste rulări vii. Pornirea de la început e a
  // butonului din colț, «Regenerează de la zero», care le face pe toate trei.
  const actiuneAntet = areCeOpri ? "opreste"
    : registru.solutii.length > 0 ? "sterge" : null;

  /** Câte soluții pleacă, pe etape, pentru confirmare. */
  const deSters = () => {
    const rs = registru?.solutii ?? [];
    const el = elementeStructura(rs);
    const n = {
      situatie: rs.filter((s) => s.discipline === "site_plan").length,
      arhitectura: rs.filter((s) => s.discipline === "architecture").length,
      schite: el.filter((e) => e.fel === "schita").length,
      solutii: el.filter((e) => e.fel === "solutie").length,
    };
    return { ...n, n: n.situatie + n.arhitectura + n.schite + n.solutii };
  };

  async function stergeTot() {
    if (!registru) return;
    if (!confirm(t("setari.confirma_sterge_solutii", deSters()))) return;
    setSterg(true);
    setMesaj("");
    try {
      const r = await api.deleteAllSolutions(projectId, "all");
      await reciteste();
      setMesaj(t("setari.sterse_solutii", { solutii: r.solutii, joburi: r.joburi,
                                           artefacte: r.artefacte }));
    } catch (err) {
      setMesaj(err instanceof api.ApiError && err.message ? err.message
        : t("setari.esec_sterge_solutii"));
    } finally {
      setSterg(false);
    }
  }

  // PORNIREA DE LA ÎNCEPUT, cum o face configurarea proiectului. Proiectul
  // complet: planul de situație, ca ultimul pas al temei (`pages/Fatada.tsx`),
  // iar înlănțuirea duce arhitectura și schițele după el. Proiectul de
  // structură: importul planșelor din dosar, cu căutarea structurii după el,
  // ca «Generează structura» din `pages/Dossier.tsx`.
  async function pornesteDeLaInceput() {
    if (tip === "structura") {
      await api.startJob(projectId, "external_architecture", { cautare_structura: true });
      await reciteste();
      navigate(`/p/${projectId}/arhitectura`);
    } else {
      await api.startJob(projectId, "site_plan");
      await reciteste();
      navigate(`/p/${projectId}/situatie`);
    }
  }

  // «REGENEREAZĂ DE LA ZERO» (Stefan, 28.09.2026): un buton dedicat, în colțul
  // din dreapta, care face dintr-un clic ce făcea butonul din antet în trei:
  // oprește tot ce rulează, șterge TOATE soluțiile și pornește lanțul ca
  // după configurarea proiectului. Oprirea vine întâi și pe proiect, nu doar
  // pe soluțiile șterse: o rulare fără rând încă (un plan de situație abia
  // pornit) ar ține garda «deja rulează» a pornirii.
  async function regenereaza() {
    if (!registru) return;
    const n = deSters();
    const areCeSterge = n.n > 0;
    if ((areCeSterge || areCeOpri)
        && !confirm(t("ansamblu.confirma_regenereaza", n))) return;
    setRegenerez(true);
    setMesaj("");
    try {
      if (areCeOpri || registru.rulari_fara_solutie.length > 0) {
        await api.stopAllJobs(projectId);
      }
      if (areCeSterge) await api.deleteAllSolutions(projectId, "all");
      await pornesteDeLaInceput();
    } catch (err) {
      await reciteste().catch(() => undefined);
      setMesaj(err instanceof api.ApiError && err.message ? err.message
        : t("ansamblu.esec_regenereaza"));
    } finally {
      setRegenerez(false);
    }
  }

  async function opresteTot() {
    setOpreste(true);
    setMesaj("");
    try {
      const r = await api.stopAllJobs(projectId);
      const parti: string[] = [];
      if (r.anulate) parti.push(t("solutii.oprite", { n: r.anulate }));
      if (r.oprire_ceruta) parti.push(t("solutii.in_oprire", { n: r.oprire_ceruta }));
      if (r.nu_se_pot_opri.length) {
        parti.push(t("solutii.nu_se_pot_opri", { lista: r.nu_se_pot_opri.join(", ") }));
      }
      setMesaj(parti.length ? parti.join(" · ") : t("solutii.nimic_oprit"));
    } catch (err) {
      setMesaj(err instanceof api.ApiError && err.message ? err.message : t("solutii.esec_oprire"));
    } finally {
      setOpreste(false);
    }
  }

  const deschide = (r: Rand) => {
    if (r.fel === "solutie") navigate(caleRand(projectId, r.rand));
    else if (r.fel === "element") navigate(caleRand(projectId, randulDeschis(r.element)));
  };

  // --- plăcile de etapă -------------------------------------------------------
  const detaliiPlaca = (n: NumarEtapa) => {
    const parti: string[] = [];
    if (n.gata) parti.push(t("comutator.gata", { n: n.gata }));
    if (n.inLucru) parti.push(t("lant.in_lucru", { n: n.inLucru }));
    if (n.asteapta) parti.push(t("lant.asteapta", { n: n.asteapta }));
    if (n.nuTrec) parti.push(t("lant.nu_trec", { n: n.nuTrec }));
    return parti.join(" · ") || t("ansamblu.nimic_inca");
  };
  const placa = (cheie: Filtru, eticheta: string, valoare: string, detalii: string) => (
    <button key={String(cheie)} type="button" aria-pressed={filtru === cheie}
            className={`ans-placa${filtru === cheie ? " on" : ""}`}
            onClick={() => setFiltru(filtru === cheie ? null : cheie)}>
      <span className="lbl">{eticheta}</span>
      <span className="ans-placa-valoare">{valoare}</span>
      <span className="ans-placa-detalii">{detalii}</span>
    </button>
  );

  // --- un rând al arborelui -----------------------------------------------------
  /** Ce se citește la hover pe «parcare x/y»: locurile din subsol și de la sol,
   *  necesarul și regula lui — pe rând nu încape decât raportul. */
  const titluCifrei = (r: Rand): string | null => {
    if (r.fel !== "solutie") return null;
    const p = r.rand.summary.parcare;
    if (!p || p.necesar == null) return null;
    const regula = t(r.rand.discipline === "site_plan"
      ? "ansamblu.parcare_regula_site_plan" : "ansamblu.parcare_regula_architecture");
    if (p.livrat == null) return t("ansamblu.parcare_necesar_titlu", { necesar: p.necesar, regula });
    return t("ansamblu.parcare_titlu", { subsol: p.subsol ?? 0, sol: p.sol ?? 0, necesar: p.necesar, regula });
  };
  const cifraCheie = (r: Rand): string => {
    if (r.fel === "fantoma") return "—";
    if (r.fel === "solutie") {
      const s = r.rand.summary;
      if (s.parcare?.necesar != null) {
        return s.parcare.livrat != null
          ? t("ansamblu.parcare", { livrat: s.parcare.livrat, necesar: s.parcare.necesar })
          : t("ansamblu.parcare_necesar", { necesar: s.parcare.necesar });
      }
      if (s.apartamente != null) return tn("ansamblu.apartamente#n", s.apartamente);
      if (s.headline) return `${s.headline.label} ${s.headline.value ?? "—"} ${s.headline.unit}`;
      return "—";
    }
    const e = r.element;
    const randCifre = e.fel === "schita" ? e.schita
      : (e.detaliu?.status === "ready" ? e.detaliu : e.macro);
    const c = randCifre.structura?.cifre;
    if (c?.headline && c.headline.value != null) {
      return `${c.headline.label} ${numar(c.headline.value, { maximumFractionDigits: 2 })} ${c.headline.unit}`;
    }
    const esv = c?.esv_pe_nivel ? Object.entries(c.esv_pe_nivel)[0] : null;
    if (esv) return t("struct.rez.esv_nivel", { n: esv[1], corp: esv[0] });
    return "—";
  };
  const cost = (r: Rand): ReactNode => {
    if (r.fel !== "element" || r.element.fel !== "solutie") return "—";
    const d = r.element.detaliu;
    const c = d?.status === "ready" ? d.structura?.cost?.direct_eur : null;
    return c != null && d
      ? <Link to={caleCosturi(projectId, d.id)} onClick={(e) => e.stopPropagation()}>{money(c)}</Link>
      : "—";
  };

  const butonInline = (r: Rand): ReactNode => {
    if (!potScrie || r.fel !== "element") return null;
    const e = r.element;
    if (e.fel === "schita") {
      if (!e.arhitectura) return null;
      const arh = e.arhitectura;
      // pe o arhitectură încap o soluție generată și una importată din planșe:
      // fiecare buton stă cât lipsește soluția de felul lui
      const genereaza = poateGeneraSolutia(registru.solutii, arh);
      const importa = poateGeneraSolutia(registru.solutii, arh, "structurist");
      if (!genereaza && !importa) return null;
      const areDesenata = areStructuraDesenata(registru.solutii, arh);
      return (
        <>
          {genereaza && (
            <button type="button" className="btn ans-buton" disabled={!!actiuni.ocupat}
                    onClick={(ev) => { ev.stopPropagation(); void actiuni.genereazaSolutia(arh); }}>
              {t("actiune.genereaza")}
            </button>
          )}
          {importa && (
            <button type="button" className="btn ans-buton" disabled={!!actiuni.ocupat || !areDesenata}
                    title={t(areDesenata ? "bloc.importa_titlu" : "bloc.import_fara_structura")}
                    onClick={(ev) => { ev.stopPropagation(); void actiuni.importaStructura(arh); }}>
              {t("actiune.importa")}
            </button>
          )}
        </>
      );
    }
    if (!poatePornireDetaliu(e)) return null;
    return (
      <button type="button" className="btn btnp ans-buton" disabled={!!actiuni.ocupat}
              onClick={(ev) => { ev.stopPropagation(); void actiuni.pornesteDetaliat(e.macro); }}>
        {t("actiune.porneste_detaliat")}
      </button>
    );
  };

  const meniuRand = (r: Rand): ReactNode => {
    if (r.fel === "fantoma") return null;
    const cheie = cheieRand(r);
    const rand = r.fel === "solutie" ? r.rand
      : r.element.fel === "schita" ? r.element.schita : r.element.macro;
    const deschis = meniu === cheie;
    const inchide = () => setMeniu(null);
    const optiuni: ReactNode[] = [];
    if (rand.discipline !== "structure" && !rand.promoted && rand.status === "ready") {
      optiuni.push(<button key="stea" type="button" role="menuitem" className="meniu-rand"
                           onClick={() => { inchide(); void actiuni.promoveaza(rand); }}>
        ★ {t("bloc.fa_favorita")}</button>);
    }
    if (rand.discipline === "site_plan" && rand.status === "ready") {
      optiuni.push(<button key="arh" type="button" role="menuitem" className="meniu-rand"
                           onClick={() => { inchide();
                             void actiuni.pornesteJob(`arh:${rand.id}`, "architecture",
                                                      { upstream_solution_id: rand.id }); }}>
        {t("ansamblu.varianta_arhitectura")}</button>);
    }
    if (rand.discipline === "architecture") {
      optiuni.push(<button key="schita" type="button" role="menuitem" className="meniu-rand"
                           onClick={() => { inchide(); void actiuni.reiaSchita(rand.id); }}>
        {t("actiune.reia_schita")}</button>);
      // pasul 2 din nou: subsolul se reface pe același rând, etajele rămân
      if (poateRefaceSubsolul(rand)) {
        optiuni.push(<button key="subsol" type="button" role="menuitem" className="meniu-rand"
                             title={t("bloc.subsol_titlu")}
                             onClick={() => { inchide();
                               void actiuni.pornesteJob(`subsol:${rand.id}`,
                                                        "basement_architecture",
                                                        { upstream_solution_id: rand.id }); }}>
          {t("bloc.refa_subsolul")}</button>);
      }
    }
    if (r.fel === "element" && r.element.fel === "solutie") {
      const m = r.element.macro;
      if (m.sursa !== "om" && !eDeschis(m.stare)) {
        optiuni.push(<button key="rerul" type="button" role="menuitem" className="meniu-rand"
                             onClick={() => { inchide(); void actiuni.reruleaza(m); }}>
          {t("actiune.reruleaza")}</button>);
      }
    }
    if (r.fel === "element" && r.element.fel === "schita" && r.element.arhitectura) {
      const arh = r.element.arhitectura;
      optiuni.push(<button key="reia" type="button" role="menuitem" className="meniu-rand"
                           onClick={() => { inchide(); void actiuni.reiaSchita(arh); }}>
        {t("actiune.reia_schita")}</button>);
    }
    const viu = r.fel === "element"
      ? (r.element.fel === "schita" ? [r.element.schita] : [r.element.macro, r.element.detaliu])
          .find((x) => x?.rulare && eDeschis(x.stare)) ?? null
      : rand.rulare && eDeschis(rand.stare) ? rand : null;
    if (viu) {
      optiuni.push(<button key="stop" type="button" role="menuitem" className="meniu-rand"
                           onClick={() => { inchide(); void actiuni.opreste(viu); }}>
        {t("actiune.opreste")}</button>);
    }
    // lucrările de după etaje: rândul e «gata», deci n-are «Oprește» al lui
    if (r.fel === "solutie") {
      for (const l of lucrariVii(r.rand)) {
        if (viu?.rulare?.job_id === l.job_id) continue;
        optiuni.push(<button key={`stop:${l.job_id}`} type="button" role="menuitem"
                             className="meniu-rand"
                             onClick={() => { inchide(); void actiuni.opresteLucrarea(l); }}>
          {t("ansamblu.opreste_lucrarea", { lucrare: numeLucrare(l).toLowerCase() })}</button>);
      }
    }
    // S0 nu se șterge singur (veriga l-ar renaște): se folosește «Reia schița»
    if (!(r.fel === "element" && r.element.fel === "schita")) {
      optiuni.push(<button key="sterge" type="button" role="menuitem" className="meniu-rand pericol"
                           onClick={() => { inchide(); void actiuni.sterge(rand); }}>
        {t("actiune.sterge_ramura")}</button>);
    }
    if (!potScrie) return null;
    return (
      <span className="ans-meniu">
        <button type="button" className="ans-puncte" aria-haspopup="menu" aria-expanded={deschis}
                aria-label={t("ansamblu.actiuni_pentru", { nume: numeRandului(r) })}
                onClick={(e) => { e.stopPropagation(); setMeniu(deschis ? null : cheie); }}>⋯</button>
        {deschis && (
          <MeniuLista laInchide={inchide} onClick={(e) => e.stopPropagation()}
                      onMouseLeave={inchide}>
            {optiuni}
          </MeniuLista>
        )}
      </span>
    );
  };

  const numeRandului = (r: Rand) =>
    r.fel === "solutie" ? numeRand(r.rand)
      : r.fel === "element" ? numeElement(r.element)
      : t("nav.fantoma", { etapa: numeEtapa(r.disciplina) });

  const rand = (r: Rand) => {
    const cheie = cheieRand(r);
    const selectat = cheie === ales && r.fel !== "fantoma";
    const faze = r.fel === "solutie" ? fazeleRandului(r.rand)
      : r.fel === "element" ? fazeleElementului(r.element)
      : [{ cheie: "f", nume: t("stare.in_lucru"), stare: "in_lucru" as const,
           nota: null }];
    const chips: ReactNode[] = [];
    if (r.fel === "solutie" && r.rand.promoted) {
      chips.push(<span key="stea" className="nav-chip acc">★ {t("bloc.favorita")}</span>);
    }
    // CE SE LUCREAZĂ PE O ARHITECTURĂ GATA: rândul se deschide, dar se schimbă
    // sub ochii omului — eticheta o spune pe rând, nu doar în bara fazelor
    if (r.fel === "solutie") {
      for (const l of r.rand.lucrari ?? []) {
        if (eDeschis(l.stare)) {
          chips.push(<span key={`l:${l.job_id}`} className="nav-chip acc">
            {t("ansamblu.lucrare_vie", { lucrare: numeLucrare(l).toLowerCase() })}</span>);
        } else if (l.stare === "picata") {
          chips.push(<span key={`l:${l.job_id}`} className="nav-chip bad">
            {t("ansamblu.lucrare_picata", { lucrare: numeLucrare(l).toLowerCase() })}</span>);
        }
      }
    }
    if (r.fel === "element" && r.element.fel === "solutie" && r.element.macro.structura?.editare) {
      const ed = r.element.macro.structura.editare;
      const din = elementulRandului(registru.solutii, ed.din);
      chips.push(<span key="ed" className="nav-chip met">
        {din ? t("bloc.editata_din", { solutie: numeElement(din), n: ed.blocate })
          : t("bloc.editata_blocate", { n: ed.blocate })}</span>);
    }
    if (r.fel === "element" && r.element.fel === "solutie" && r.element.macro.sursa === "structurist") {
      chips.push(<span key="imp" className="nav-chip met" title={t("bloc.importata_titlu")}>
        {t("bloc.importata")}</span>);
    }
    if (r.fel === "element" && r.element.fel === "schita"
        && r.element.schita.structura?.arhitectura_editata_la) {
      chips.push(<span key="ns" className="nav-chip amb">⚠ {t("bloc.nesincronizata")}</span>);
    }
    const corpuriSarite = r.fel === "element"
      ? randulDeschis(r.element).structura?.corpuri.filter((c) => c.stare === "sarit") ?? [] : [];
    if (corpuriSarite.length) {
      chips.push(<span key="sar" className="nav-chip mut">
        {t("ansamblu.corp_sarit", { cod: corpuriSarite.map((c) => c.cod).join(", ") })}</span>);
    }
    return (
      <tr key={cheie}
          className={`${selectat ? "ales" : ""}${r.fel !== "fantoma" && r.context ? " context" : ""}`}
          onClick={() => { if (r.fel !== "fantoma") comutaAles(cheie); }}
          onDoubleClick={() => deschide(r)}>
        <td style={{ paddingLeft: 12 + 24 * r.nivel }}>
          <span className="ans-nume">
            {r.nivel > 0 && <span className="ans-ramura" aria-hidden="true">└ </span>}
            <button type="button" className="ans-nume-buton"
                    onClick={(e) => { e.stopPropagation(); comutaAles(cheie); }}>
              {numeRandului(r)}
            </button>
            {chips}
          </span>
        </td>
        <td>
          <span className="ans-faze">
            <FazeBara faze={faze} />
            {butonInline(r)}
          </span>
        </td>
        <td className="ans-cifra" title={titluCifrei(r) ?? undefined}>{cifraCheie(r)}</td>
        <td className="ans-cost">{cost(r)}</td>
        <td>{meniuRand(r)}</td>
      </tr>
    );
  };

  return (
    <div className="ans-pagina">
      <main className="ans-corp scroll">
        <div className="ans-antet">
          <div>
            <h1 className="disp">{t("ansamblu.titlu")}</h1>
            <span className="ans-sub">
              {[tn("ansamblu.solutii#n", total),
                inLucru ? t("lant.in_lucru", { n: inLucru }) : null,
                asteapta ? t("lant.asteapta", { n: asteapta }) : null].filter(Boolean).join(" · ")}
            </span>
          </div>
          <label className="ans-interne">
            <input type="checkbox" checked={interne} onChange={(e) => setInterne(e.target.checked)} />
            {t("ansamblu.interne")}
          </label>
          {actiuneAntet === "opreste" && (
            <button type="button" className="btn" onClick={opresteTot}
                    disabled={!potScrie || opreste}
                    title={t(!potScrie ? "comun.doar_citire" : "solutii.opreste_tot_titlu")}>
              {t(opreste ? "solutii.se_opreste" : "ansamblu.opreste_tot")}
            </button>
          )}
          {actiuneAntet === "sterge" && (
            <button type="button" className="btn" onClick={stergeTot}
                    disabled={!potScrie || sterg}
                    title={t(!potScrie ? "comun.doar_citire" : "setari.sterge_solutii_titlu")}>
              {t(sterg ? "ansamblu.se_sterg" : "setari.sterge_solutii")}
            </button>
          )}
          <button type="button" className="btn btnp ans-regenereaza" onClick={regenereaza}
                  disabled={!potScrie || regenerez || opreste || sterg}
                  title={t(!potScrie ? "comun.doar_citire"
                    : tip === "structura" ? "ansamblu.regenereaza_titlu_structura"
                    : "ansamblu.regenereaza_titlu")}>
            {t(regenerez ? "ansamblu.se_regenereaza" : "ansamblu.regenereaza")}
          </button>
        </div>
        <BandaDoarCitire />
        {(mesaj || actiuni.eroare) && <p className="note ans-mesaj">{actiuni.eroare || mesaj}</p>}

        <div className="ans-placi">
          {placa("site_plan", numeEtapa("site_plan"),
                 tn("ansamblu.amplasari#n", nr.site_plan.total), detaliiPlaca(nr.site_plan))}
          {placa("architecture", numeEtapa("architecture"),
                 tn("ansamblu.arhitecturi#n", nr.architecture.total), detaliiPlaca(nr.architecture))}
          {placa("structure", numeEtapa("structure"),
                 `${tn("ansamblu.schite#n", nr.structure.schite)} · ${tn("ansamblu.solutii_str#n", nr.structure.solutii)}`,
                 detaliiPlaca(nr.structure))}
          {placa("costuri", numeEtapa("costuri"), String(nr.costuri.total),
                 nr.costuri.total ? t("ansamblu.costuri_gata") : t("ansamblu.costuri_dupa"))}
        </div>

        {/* Bandă îngustă (Corecția lui Stefan): întâi ce macină, apoi ce
            așteaptă — fiecare pe linia lui, fiecare link (A1) —, apoi ce s-a
            terminat de curând. */}
        {lucru.length > 0 && (
          <div className="ans-lucru" aria-label={t("ansamblu.in_lucru_acum")}>
            <span className="lbl">{t("ansamblu.in_lucru_acum")}</span>
            {[...lucru.filter((l) => l.stare !== "asteapta"),
              ...lucru.filter((l) => l.stare === "asteapta")].map((l) => (
              <Link key={l.id} to={l.href}
                    className={`ans-lucru-link${l.stare === "asteapta" ? " mut" : ""}`}>
                <SemnStare stare={l.stare} s={12} />
                {[l.nume + (l.pe ? ` ${l.pe}` : ""), l.faza, l.nota].filter(Boolean).join(" · ")}
              </Link>
            ))}
          </div>
        )}
        {terminate.length > 0 && (
          <div className="ans-lucru ans-terminate" aria-label={t("ansamblu.terminate_recent")}>
            <span className="lbl">{t("ansamblu.terminate_recent")}</span>
            {terminate.map((l) => (
              <Link key={l.id} to={l.href} className="ans-lucru-link">
                <SemnStare stare={l.stare} s={12} />
                {[l.nume + (l.pe ? ` ${l.pe}` : ""), l.faza, l.nota, ora(l.la)]
                  .filter(Boolean).join(" · ")}
              </Link>
            ))}
          </div>
        )}

        <section className="ans-arbore">
          <table>
            <thead>
              <tr>
                <th style={{ width: 300 }}>{t("ansamblu.col_solutie")}</th>
                <th>{t("ansamblu.col_faze")}</th>
                <th style={{ width: 160 }}>{t("ansamblu.col_cifra")}</th>
                <th style={{ width: 80 }}>{t("ansamblu.col_cost")}</th>
                <th style={{ width: 44 }} aria-label={t("ansamblu.col_actiuni")} />
              </tr>
            </thead>
            <tbody>
              {randuri.length ? randuri.map(rand) : (
                <tr><td colSpan={5} className="note">{t("ansamblu.gol")}</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      {randAles ? <Previzualizare r={randAles} onInchide={() => setAles(null)} />
        : <EvenimenteProiect />}
    </div>
  );
}

/** «Lucrări pe această arhitectură»: ce a scris pe ea după etaje, cu ora și
 *  durata fiecăreia, cea vie întâi la vedere și cu butonul ei de oprire. */
function Lucrari({ rand }: { rand: SolutieRand }) {
  const t = useT();
  const tn = useTn();
  const { potScrie } = useRolProiect();
  const actiuni = useActiuni();
  const lucrari = rand.lucrari ?? [];
  return (
    <div className="ans-lucrari" aria-label={t("ansamblu.lucrari_titlu")}>
      <span className="lbl">{t("ansamblu.lucrari_titlu")}</span>
      {lucrari.map((l) => {
        const viu = eDeschis(l.stare);
        return (
          <div key={l.job_id} className={`ans-lucrare${viu ? " vie" : ""}`}>
            <SemnStare stare={l.stare} />
            <span className="ans-lucrare-text">
              <b>{numeLucrare(l)}</b>
              <span className="ans-lucrare-nota">
                {[notaLucrarii(l),
                  l.incercari_picate > 0 ? tn("ansamblu.incercari_picate#n", l.incercari_picate) : null]
                  .filter(Boolean).join(" · ")}
              </span>
            </span>
            {viu && potScrie && (
              <button type="button" className="btn ans-buton" disabled={!!actiuni.ocupat}
                      onClick={() => actiuni.opresteLucrarea(l)}>
                {t("actiune.opreste")}
              </button>
            )}
          </div>
        );
      })}
      {lucrari.some((l) => eDeschis(l.stare)) && (
        <p className="note">{t("ansamblu.lucrare_nota")}</p>
      )}
    </div>
  );
}

/** Previzualizarea rândului ales: volumul clădirii în mic, fazele, cifrele
 *  cheie și «Deschide soluția» / «Pornește calculul detaliat». */
function Previzualizare({ r, onInchide }: { r: Rand; onInchide: () => void }) {
  const t = useT();
  const { registru, projectId } = useRegistru();
  const { potScrie } = useRolProiect();
  const actiuni = useActiuni();
  if (!registru || r.fel === "fantoma") return null;
  const randPrivit = r.fel === "solutie" ? r.rand : randulDeschis(r.element);
  const nume = r.fel === "solutie" ? numeRand(r.rand) : numeElement(r.element);
  const faze = r.fel === "solutie" ? fazeleRandului(r.rand) : fazeleElementului(r.element);
  const arhId = randPrivit.discipline === "architecture" ? randPrivit.id
    : randPrivit.discipline === "structure" ? randPrivit.lineage.architecture
    : registru.implicit.architecture_pe_site_plan[randPrivit.id] ?? null;
  const arh = arhId ? registru.solutii.find((s) => s.id === arhId) : null;
  const amonte = randPrivit.discipline === "structure" ? arh
    : randPrivit.discipline === "architecture"
      ? registru.solutii.find((s) => s.id === randPrivit.lineage.site_plan) ?? null : null;

  // [etichetă, valoare, ton]: tonul «amb» e parcarea care nu ajunge
  const randuriCifre: [string, string, ("amb" | null)?][] = [];
  if (r.fel === "element" && r.element.fel === "solutie") {
    const m = r.element.macro;
    for (const v of m.structura?.poarta?.verificari ?? []) {
      if (v.valoare == null) continue;
      const u = v.unitate ?? "";
      randuriCifre.push([
        `${numeVerificarePoarta(v.rule_code)}${v.directie ? ` ${v.directie}` : ""}`,
        `${numar(v.valoare, { maximumFractionDigits: 2 })}${u ? ` ${u}` : ""}`
          + (v.limita != null ? ` · ${t("struct.rez.limita", { v: `${numar(v.limita)}${u ? ` ${u}` : ""}` })}` : ""),
      ]);
    }
    const c = (r.element.detaliu?.status === "ready" ? r.element.detaliu : m).structura?.cifre;
    if (c?.headline && c.headline.value != null) {
      randuriCifre.push([c.headline.label, `${numar(c.headline.value, { maximumFractionDigits: 2 })} ${c.headline.unit}`]);
    }
    const f = c?.fundare ? Object.values(c.fundare)[0] : null;
    if (f) randuriCifre.push([t("struct.rez.fundare"),
      f.h_r_m != null ? `${f.sistem}, ${numar(f.h_r_m, { maximumFractionDigits: 2 })} m` : f.sistem]);
    const cost = r.element.detaliu?.status === "ready" ? r.element.detaliu.structura?.cost : null;
    randuriCifre.push([t("ansamblu.col_cost"),
      cost?.direct_eur != null ? money(cost.direct_eur) : t("lant.dupa_detaliu")]);
  } else if (r.fel === "solutie") {
    const s = r.rand.summary;
    if (s.apartamente != null) randuriCifre.push([t("ansamblu.apartamente"), String(s.apartamente)]);
    if (s.niveluri != null) randuriCifre.push([t("ansamblu.niveluri"), String(s.niveluri)]);
    if (s.parcare?.necesar != null) {
      const nuAjung = s.parcare.livrat == null || s.parcare.livrat < s.parcare.necesar;
      randuriCifre.push([t("ansamblu.parcare_eticheta"),
        `${s.parcare.livrat ?? "—"} / ${s.parcare.necesar}`, nuAjung ? "amb" : null]);
      if (s.parcare.livrat != null) {
        randuriCifre.push([t("ansamblu.parcare_subsol"), String(s.parcare.subsol ?? 0)]);
        randuriCifre.push([t("ansamblu.parcare_sol"), String(s.parcare.sol ?? 0)]);
      }
    }
    // statisticile pasului 2 al planului (raportul circulației), cât sunt scrise
    const c = s.circulatie;
    if (c) {
      if (c.verde_total_mp != null) {
        randuriCifre.push([t("ansamblu.verde_total"), `${numar(c.verde_total_mp, { maximumFractionDigits: 0 })} m²`]);
      }
      if (c.verde_pe_sol_mp != null) {
        randuriCifre.push([t("ansamblu.verde_pe_sol"), `${numar(c.verde_pe_sol_mp, { maximumFractionDigits: 0 })} m²`]);
      }
      if (c.rampe != null) {
        randuriCifre.push([t("ansamblu.rampe_legate"), `${c.rampe_legate ?? 0} / ${c.rampe}`,
                           (c.rampe_legate ?? 0) < c.rampe ? "amb" : null]);
      }
    }
  }

  const el = r.fel === "element" ? r.element : null;
  const poateDetaliat = poatePornireDetaliu(el);
  return (
    <aside className="ans-previzualizare" aria-label={t("ansamblu.previzualizare_aria", { nume })}>
      <div className="ans-previz-cap">
        <button type="button" className="ans-previz-inchide" onClick={onInchide}
                title={t("ansamblu.inchide_previzualizarea")}
                aria-label={t("ansamblu.inchide_previzualizarea")}>×</button>
        <span className="lbl">
          {t("ansamblu.previzualizare", {
            etapa: numeEtapa(randPrivit.discipline).toLowerCase(),
            pe: amonte ? t("nav.pe", { solutie: numeRand(amonte) }) : "",
          })}
        </span>
        <h2 className="disp">{nume}</h2>
      </div>
      {/* chiar soluția: situația și arhitectura de sus, structura în volum */}
      <div className="ans-volum">
        <PrevizSolutie disciplina={randPrivit.discipline} cheie={randPrivit.id}
                       niveluri={arh?.summary.niveluri ?? null}
                       silueta={arh?.summary.silueta ?? null} w={336} h={170} />
      </div>
      <FazeBara faze={faze} />
      {r.fel === "solutie" && (r.rand.lucrari?.length ?? 0) > 0 && (
        <Lucrari rand={r.rand} />
      )}
      {randuriCifre.length > 0 && (
        <div className="ans-cifre">
          {randuriCifre.map(([k, v, ton]) => (
            <div key={k} className={`ans-placuta${ton ? ` ${ton}` : ""}`}>
              <span className="lbl">{k}</span>
              <b>{v}</b>
            </div>
          ))}
        </div>
      )}
      {amonte && (
        <div className="ans-rand-meta">
          <span>{t("ansamblu.pleaca_din")}</span><span>{numeRand(amonte)}</span>
        </div>
      )}
      <div className="ans-previz-butoane">
        <Link className="btn btnp" to={caleRand(projectId, randPrivit)}>
          {t("ansamblu.deschide")}
        </Link>
        {/* Parking Inspector: pe arhitectură, sub «Deschide soluția» */}
        {randPrivit.discipline === "architecture" && (
          <Link className="btn btn-insp" to={`/p/${projectId}/incercari/${randPrivit.id}`}
                title={t("bloc.incercari_parcare_titlu")}>
            <Ic n="bug" s={14} />{t("bloc.incercari_parcare")}
          </Link>
        )}
        {/* Excavația: ecranul de depanare al așezării excavației pe benzi, lângă Parking Inspector */}
        {randPrivit.discipline === "architecture" && (
          <Link className="btn btn-insp" to={`/p/${projectId}/excavatie/${randPrivit.id}`}
                title={t("bloc.excavatia_titlu")}>
            <Ic n="bug" s={14} />{t("bloc.excavatia")}
          </Link>
        )}
        {poateDetaliat && el?.fel === "solutie" && potScrie && (
          <button type="button" className="btn" disabled={!!actiuni.ocupat}
                  onClick={() => actiuni.pornesteDetaliat(el.macro)}>
            {t("actiune.porneste_detaliat")}
          </button>
        )}
        {actiuni.eroare && <p className="note bad">{actiuni.eroare}</p>}
      </div>
      {el && eDeschis(stareElement(el)) && (
        <p className="note">{t("ansamblu.in_lucru_nota")}</p>
      )}
    </aside>
  );
}
